import { Component, OnInit } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, of, Subscription } from 'rxjs'
import {
  catchError,
  filter,
  first,
  map,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { CompetitionMatch } from '../../competition-match/competition-match'
import { CompetitionPlayer } from '../../competition-player/competition-player'
import { competitionPlayerActions } from '../../competition-player/competition-player.actions'
import { competitionPlayerSelectors } from '../../competition-player/competition-player.selectors'
import { CompetitionStaffFunction } from '../../competition-staff-function/competition-staff-function'
import { competitionStaffFunctionActions } from '../../competition-staff-function/competition-staff-function.actions'
import { competitionStaffFunctionSelectors } from '../../competition-staff-function/competition-staff-function.selectors'
import { CompetitionStaff } from '../../competition-staff/competition-staff'
import { competitionStaffActions } from '../../competition-staff/competition-staff.actions'
import { competitionStaffSelectors } from '../../competition-staff/competition-staff.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionTeamAddMemberModalComponent } from '../add-member-modal/show.component'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamActions } from '../competition-team.actions'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { CompetitionTeamService } from '../competition-team.service'
import { environment } from 'src/environments/environment'
import { ActivatedRoute } from '@angular/router'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { TranslateService } from '@ngx-translate/core'
@UntilDestroy()
@Component({
  selector: 'competition-team-overview-show',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamOverviewComponent implements OnInit {
  public isLoading$: Observable<boolean>
  public selectedTab: string = environment.moduleConfigs.competitionModule
    .showOnlyCompetitionStanding
    ? 'tables'
    : 'matches'
  public memberEntities$: Observable<Dictionary<Member>>
  public matches$: Observable<CompetitionMatch[]>
  public member$: Observable<Member>
  public isAbleToEditTeam$: Observable<boolean>
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public teamIsEditable: boolean = false
  public competitionStaff$: Observable<CompetitionStaff[]>
  public competitionPlayers$: Observable<CompetitionPlayer[]>
  public competitionStaffFunctionEntities$: Observable<
    Dictionary<CompetitionStaffFunction>
  >
  public bsModalRef: BsModalRef
  public competitionStaffFunctions$: Observable<CompetitionStaffFunction[]>
  public showOnlyCompetitionStanding =
    environment.moduleConfigs.competitionModule.showOnlyCompetitionStanding
  private competitionId: number
  private teamId: number
  private subscriptions: Subscription[] = []
  public appRoutesNS = AppRoutesNS
  constructor(
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    private actions$: Actions,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    private competitionTeamService: CompetitionTeamService,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.store.dispatch(
      BreadCrumbActions.addPageToHistory({
        pageName: 'general.back',
        url: AppRoutesNS.Competitions.COMPETITIONS,
        showBackArrow: true,
        titleName: 'competitionModule.competitions'
      })
    )
    this.isLoading$ = combineLatest([
      this.store
        .select(competitionTeamSelectors.selectIsLoading)
        .pipe(startWith(false)),
      this.store
        .select(competitionStaffSelectors.selectIsLoading)
        .pipe(startWith(false)),
      this.store
        .select(competitionPlayerSelectors.selectIsLoading)
        .pipe(startWith(false))
    ]).pipe(
      map(
        ([
          isLoadingCompetitionTeam,
          isLoadingCompetitionStaff,
          isLoadingCompetitionPlayer
        ]) => {
          return Boolean(
            isLoadingCompetitionTeam ||
              isLoadingCompetitionStaff ||
              isLoadingCompetitionPlayer
          )
        }
      )
    )

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id)),
      tap((id) => {
        this.teamId = id
      })
    )

    this.teamId$.pipe(take(1)).subscribe((id) => {
      this.store.dispatch(
        competitionTeamActions.fetchOneById({
          id,
          params: RequestQueryBuilder.create({
            join: [
              { field: 'competitions' },
              { field: 'competitions.competitionSport' },
              { field: 'competitionStaff' }
            ]
          })
        })
      )
    })

    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      )
    )

    this.competitionStaffFunctions$ = combineLatest([
      this.team$,
      this.store.select(competitionSelectors.selectEntities)
    ]).pipe(
      untilDestroyed(this),
      map(
        ([team, competitionEntities]) =>
          competitionEntities[team?.competitionId]?.competitionSportId
      ),
      tap((competitionSportId) => {
        if (competitionSportId) {
          this.store.dispatch(
            competitionStaffFunctionActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  competitionSportId
                }
              })
            })
          )
        }
      }),
      switchMap((competitionSportId) =>
        this.store.select(competitionStaffFunctionSelectors.selectByFilter, {
          field: 'competitionSportId',
          value: competitionSportId
        })
      )
    )

    this.competitionStaffFunctionEntities$ = this.store.select(
      competitionStaffFunctionSelectors.selectEntities
    )

    this.competitionStaff$ = combineLatest([
      this.store.select(competitionStaffSelectors.selectByFilter, {
        field: 'competitionTeamId',
        value: this.teamId
      }),
      this.store.select(competitionTeamSelectors.selectInvitedStaffMembers)
    ]).pipe(
      untilDestroyed(this),
      map(([competitionStaffMembers, invitedStaffMembers]) => {
        const invitedStaffMembersOfTeam = invitedStaffMembers.filter(
          (staff) => staff.teamId == this.teamId
        )

        return [...competitionStaffMembers, ...invitedStaffMembersOfTeam]
      })
    )

    this.competitionPlayers$ = combineLatest([
      this.store.select(competitionPlayerSelectors.selectByFilter, {
        field: 'competitionTeamId',
        value: this.teamId
      }),
      this.store.select(competitionTeamSelectors.selectInvitedPlayers)
    ]).pipe(
      untilDestroyed(this),
      map(([competitionPlayers, invitedPlayers]) => {
        const invitedPlayersOfTeam = invitedPlayers.filter(
          (player) => player.teamId == this.teamId
        )

        return [...competitionPlayers, ...invitedPlayersOfTeam]
      })
    )

    this.member$ = this.store.select(authSelectors.selectMember)
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)

    this.isAbleToEditTeam$ = combineLatest([
      this.member$,
      this.team$,
      this.store.select(competitionSelectors.selectEntities)
    ]).pipe(
      untilDestroyed(this),
      map(([member, team, competitionEntities]) => {
        if (
          moment(competitionEntities[team?.competitionId]?.endDate).isAfter(
            moment()
          )
        ) {
          if (team?.teamCaptainPersonId == member?.id) {
            return true
          }
          return false
        }
        return false
      })
    )
  }

  public onClickAddStaff() {
    this.team$.pipe(take(1)).subscribe(
      (team) =>
        (this.bsModalRef = this.modalService.show(
          CompetitionTeamAddMemberModalComponent,
          {
            initialState: {
              role: 'staff',
              team
            }
          }
        ))
    )

    this.subscriptions.push(
      this.bsModalRef.content.invitedStaffMember
        .pipe(
          take(1),
          withLatestFrom(
            this.store.select(settingSelectors.selectById, 'language'),
            this.team$,
            this.store.select(
              competitionTeamSelectors.selectInvitedStaffMembers
            )
          )
        )
        .subscribe(
          ([
            invitedStaffMember,
            selectedLanguage,
            team,
            invitedStaffMembers
          ]) => {
            if (invitedStaffMember) {
              const emailExists = invitedStaffMembers?.some(
                (item) =>
                  item.email == invitedStaffMember.email &&
                  invitedStaffMember.teamId == team?.id &&
                  item.functionId == invitedStaffMember.functionId
              )

              if (!emailExists) {
                this.store.dispatch(
                  competitionTeamActions.setInvitedStaffMembers({
                    entity: [...invitedStaffMembers, invitedStaffMember]
                  })
                )

                const templateType = 'Competition_team_registration_invite'

                this.competitionTeamService
                  .sendInvitationEmail(
                    team,
                    templateType,
                    selectedLanguage,
                    invitedStaffMember.email,
                    'staff',
                    invitedStaffMember.functionId,
                    team?.competitionId,
                    invitedStaffMember.number
                  )
                  .subscribe()
              }
            }
          }
        )
    )
  }

  public onClickAddPlayer() {
    this.team$.pipe(take(1)).subscribe(
      (team) =>
        (this.bsModalRef = this.modalService.show(
          CompetitionTeamAddMemberModalComponent,
          {
            initialState: {
              role: 'player',
              team
            }
          }
        ))
    )

    this.subscriptions.push(
      this.bsModalRef.content.invitedPlayer
        .pipe(
          take(1),
          withLatestFrom(
            this.store.select(settingSelectors.selectById, 'language'),
            this.team$,
            this.store.select(competitionTeamSelectors.selectInvitedPlayers)
          )
        )
        .subscribe(
          ([invitedPlayer, selectedLanguage, team, invitedPlayers]) => {
            if (invitedPlayer) {
              const emailExists = invitedPlayers?.some(
                (item) =>
                  item.email == invitedPlayer.email &&
                  invitedPlayer.teamId == team?.id
              )

              if (!emailExists) {
                this.store.dispatch(
                  competitionTeamActions.setInvitedPlayers({
                    entity: [...invitedPlayers, invitedPlayer]
                  })
                )

                const templateType = 'Competition_team_registration_invite'

                this.competitionTeamService
                  .sendInvitationEmail(
                    team,
                    templateType,
                    selectedLanguage,
                    invitedPlayer.email,
                    'player',
                    invitedPlayer.functionId,
                    team?.competitionId,
                    invitedPlayer.number
                  )
                  .subscribe()
              }
            }
          }
        )
    )
  }

  public onClickRemoveStaff(staffId: number) {
    const message = this.translate.instant(
      'competitionModule.removeStaffMember'
    )
    if (!window.confirm(message)) {
      return
    }

    this.store.dispatch(competitionStaffActions.deleteOne({ id: staffId }))

    this.actions$
      .pipe(
        ofType(competitionStaffActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onClickRemovePlayer(staffId: number) {
    const message = this.translate.instant('competitionModule.removePlayer')
    if (!window.confirm(message)) {
      return
    }

    this.store.dispatch(competitionPlayerActions.deleteOne({ id: staffId }))

    this.actions$
      .pipe(
        ofType(competitionPlayerActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
